/// <amd-module name="Wiki/PageTree/Components/WikiPagePathTree" />
import * as React from "react";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { ListSelection } from "VSSUI/List";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ITreeColumn, ITreeRow, Tree, TreeExpand } from "VSSUI/TreeEx";
import { preventDefault } from "VSSUI/Util";
import { indexWithinRanges } from "VSSUI/Utilities/Selection";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { TreeNodeDraggable } from "Wiki/PageTree/Components/TreeNodeDraggable";
import { FilteredWikiPage, WikiTreePage } from "Wiki/PageTree/Contracts";
import { WikiPageTreeItemProvider } from "Wiki/PageTree/WikiPageTreeItemProvider";
import { isInclude } from "Wiki/ViewCommon/Utils/PathHelper";
import { WikiTreeHeaderBreadcrumbService } from "Wiki/PageTree/WikiTreeHeaderBreadcrumbService";
import * as Resources from "Wiki/PageTree/Resources";
import { format } from "VSS/Core/Util/String";

export interface WikiTreeDragDropProps {
    onDrag: (source: WikiPage) => void;
    onDrop: (target: WikiPage, isHandleBrokenLinksEnabled?: boolean, isReorderOperation?: boolean, isReorderAbove?: boolean) => void;
    canDrag: (source: WikiPage) => boolean;
    canDrop: (target: WikiPage, isReorderOperation?: boolean) => boolean;
}

export interface WikiPagePathTreeProps {
    className?: string;
    itemProvider: WikiPageTreeItemProvider;
    onToggle: (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<WikiTreePage>) => void;
    showLines: boolean;
    onPathChanged: (newItem: WikiTreePage) => void;
    emptyTreeMsg: string;
    homePagePath?: string;
    selectedPagePath?: string;
    openInNewTab?: (path: string) => void;
    additionalColumns: ITreeColumn<WikiTreePage | FilteredWikiPage>[];
    getPageIsDisabled: (page: WikiPage) => boolean;

    dragDropProps?: WikiTreeDragDropProps;

    isIncludeEditingAllowed: boolean;
    wikiName: string;
    treeRef?: React.RefObject<Tree<WikiTreePage>>; // optional ref required for UT's
    isShowWikiName?: boolean;
}

interface WikiPagePathTreeState {
    selection: ListSelection;
}

const c_treeNameTextClassName = "tree-name-text";
export const RootId = 0;

// With every re-render of parent, rerender this component as well.
export class WikiPagePathTree extends VssComponent<WikiPagePathTreeProps, WikiPagePathTreeState> {
    public static getDerivedStateFromProps(nextProps: WikiPagePathTreeProps, prevState: WikiPagePathTreeState): WikiPagePathTreeState {
        if (nextProps.selectedPagePath) {
            const selectedIdx = nextProps.itemProvider.getItemIndex(nextProps.selectedPagePath);
            if (selectedIdx !== -1 && !prevState.selection.selected(selectedIdx)) {
                prevState.selection.select(selectedIdx);
            }
        } else {
            prevState.selection.clear();
        }

        return prevState; // this is also the new state as selection object is updated
    }
    private _tree = React.createRef<Tree<WikiTreePage>>();

    constructor(props: WikiPagePathTreeProps, context: IVssComponentContext) {
        super(props, context);

        const listSelection = new ListSelection({ multiSelect: false, selectOnFocus: false });
        listSelection.selectable = (index: number): boolean => {
            const page = this.props.itemProvider.getItemFromTreeIndex(index);
            if (page) {
                if (!page.subPages || page.subPages.length === 0) {
                    return !this.props.getPageIsDisabled(page);
                }

                return true;
            }
            return !indexWithinRanges(index, listSelection.unselectableRanges);
        };

        this._tree = this.props.treeRef ? this.props.treeRef : this._tree; // Required for Ut's

        this.state = {
            selection: listSelection
        };
    }

    public render(): JSX.Element | undefined {
        const itemsToDisplay = this.props.itemProvider.length;
        const nameColumn = { id: "pageName", width: -100, renderCell: this._renderTreeCell };
        const allColumns = [nameColumn, ...this.props.additionalColumns];

        return itemsToDisplay > 0 ? (
            <Tree
                scrollable={true}
                columns={allColumns}
                itemProvider={this.props.itemProvider}
                showLines={this.props.showLines}
                onToggle={this.props.onToggle}
                onActivate={this._onActivate}
                selection={this.state.selection}
                showHeader={false}
                ref={this._tree}
                ariaLabel={format(Resources.WikiPageTreeAriaLabel, this.props.wikiName.replace(".wiki", ""))}
            />
        ) : (
            <div className={"no-results-msg"}>{this.props.emptyTreeMsg}</div>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._scrollItemToView();
    }

    public componentDidUpdate(prevProps: WikiPagePathTreeProps, prevState: WikiPagePathTreeState): void {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.itemProvider != this.props.itemProvider) {
            this._scrollItemToView();
        }
    }

    private _scrollItemToView(): void {
        if (this._tree.current && this.props.selectedPagePath) {
            const selectedIdx = this.props.itemProvider.getItemIndex(this.props.selectedPagePath);
            if (selectedIdx !== -1) {
                this._tree.current.scrollIntoView(selectedIdx);
            }
        }
    }

    private _onActivate = (event: any, treeRow: ITreeRow<WikiTreePage>): void => {
        if (!this.props.getPageIsDisabled(treeRow.data.underlyingItem.data)) {
            this.props.onPathChanged(treeRow.data.underlyingItem.data);
        }
    };

    private _renderTreeCell = (
        rowIndex: number,
        columnIndex: number,
        _treeColumn: ITreeColumn<WikiTreePage>,
        treeItem: ITreeItemEx<WikiTreePage | FilteredWikiPage>,
        _ariaRowIndex?: number,
        role?: string
    ): JSX.Element => {
        const wikiItem = treeItem.underlyingItem.data;
        const isSelected = wikiItem.path === this.props.selectedPagePath;

        if (isSelected){ 
            const breadcrumbService = this.context.pageContext.getService<WikiTreeHeaderBreadcrumbService>("wikitree-header-breadcrumb-service");
            breadcrumbService.setBreadcrumbs(treeItem);
        }

        const onClick = (event: React.MouseEvent<HTMLElement>): void => {
            if (event.ctrlKey && !event.isDefaultPrevented() && this.props.openInNewTab) {
                this.props.openInNewTab(wikiItem.path);
                preventDefault(event);
            }
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} contentClassName={"no-padding"} className={"wiki-node-tree-cell"} key={"wiki-col-" + rowIndex} role={role}>
                <span className={"wiki-page-name-cell"} onClick={onClick}>
                    {(treeItem.underlyingItem.data.id === RootId && this.props.wikiName && this.props.isShowWikiName) ? (
                        <div
                            className={css(
                                "wiki-tree-root-node",
                                isSelected && "fontWeightSemiBold",
                                this.props.getPageIsDisabled(treeItem.underlyingItem.data) && "wiki-tree-node-disabled"
                            )}
                        >
                            {this.props.wikiName}
                        </div>
                    ) : (
                        <TreeExpand
                            depth={treeItem.depth}
                            expanded={treeItem.underlyingItem.expanded}
                            onClick={preventDefault}
                            onToggle={
                                wikiItem.isParentPage
                                    ? (event: React.SyntheticEvent<HTMLElement>) => {
                                          this.props.onToggle(event, treeItem);
                                      }
                                    : undefined
                            }
                        >
                            <WikiTreeNode
                                item={treeItem}
                                isDisabled={this.props.getPageIsDisabled(treeItem.underlyingItem.data)}
                                dragDropProps={this.props.dragDropProps}
                                iconName={this._getIconName(wikiItem)}
                                isSelected={isSelected}
                                highlightIndices={(wikiItem as FilteredWikiPage).highlightedIndices} // could be undefined if this is not a filtered page
                            />
                        </TreeExpand>
                    )}
                </span>
            </SimpleTableCell>
        );
    };

    private _getIconName = (page: WikiPage): string => {
        if (this.props.homePagePath === page.path) {
            return "Home";
        }

        if (this.props.isIncludeEditingAllowed && isInclude(page.path)) {
            return "FileTemplate";
        }

        return "TextDocument";
    };
}

interface IWikiTreeNodeProps {
    item: ITreeItemEx<WikiTreePage>;
    isDisabled?: boolean;
    dragDropProps?: WikiTreeDragDropProps;
    iconName: string;
    isSelected: boolean;
    highlightIndices: number[] | undefined;
}

export const WikiTreeNode = (props: IWikiTreeNodeProps): JSX.Element => {
    const item = props.item.underlyingItem.data;
    return (
        <TreeNodeDraggable
            item={item}
            canDrag={props.dragDropProps !== undefined && props.dragDropProps.canDrag(item)}
            canDrop={
                props.dragDropProps
                    ? props.dragDropProps.canDrop
                    : () => {
                          return false;
                      }
            }
            onDrop={props.dragDropProps ? props.dragDropProps.onDrop : () => {}}
            onDrag={props.dragDropProps ? props.dragDropProps.onDrag : () => {}}
            className={"wiki-tree-node text-ellipsis"}
            dataTransferValue={item.path}
        >
            <Tooltip overflowOnly={true} text={item.pageName}>
                <div className={css("text-ellipsis", props.isDisabled && "wiki-tree-node-disabled")}>
                    {item.isNonConformant && <Status className={"non-conformant-icon"} {...Statuses.Warning} key="warning" size={StatusSize.m} />}
                    <Icon className={"type-icon fontSizeML"} iconName={props.iconName} />
                    <span
                        className={css(
                            "bolt-list-cell-child flex-row flex-center bolt-list-cell-text tree-name-cell",
                            c_treeNameTextClassName,
                            props.isSelected ? "fontWeightSemiBold" : ""
                        )}
                    >
                        {props.highlightIndices && props.highlightIndices.length > 0 ? (
                            <HighlightableRowText text={item.pageName} highlightTextIndices={props.highlightIndices} />
                        ) : (
                            item.pageName
                        )}
                    </span>
                </div>
            </Tooltip>
        </TreeNodeDraggable>
    );
};

interface HighlightableRowTextProps {
    text: string;
    highlightTextIndices?: number[];
}

interface HighlightableRowTextSubItem {
    text: string;
    highlight: boolean;
}

// Exporting for UT's
export const HighlightableRowText = (props: HighlightableRowTextProps) => {
    return (
        <>
            {_format(props.text, props.highlightTextIndices).map((item, index) =>
                item.highlight ? (
                    <mark key={item.text + index.toString()} className={c_treeNameTextClassName}>
                        {item.text}
                    </mark>
                ) : (
                    <span className={c_treeNameTextClassName} key={item.text + index.toString()}>
                        {item.text}
                    </span>
                )
            )}
        </>
    );
};

/**
 * Divides the string based upon the indices that are to be highlighted. Each created subItem has a propety
 * 'highlight' which signifies whether to highlight it or not.
 * Eg. Let's say text = 'Hello', matchingIndices = [2, 3]. Then this method will return three items with text = 'He', 'll', 'o'.
 * And highlight will be set to true for 'll'.
 * Copied from: Tfs/Service/WebAccess/Presentation/Scripts/TFS/Components/HighLightableRowText.tsx
 */
function _format(text: string, matchingIndices?: number[]): HighlightableRowTextSubItem[] {
    if (!text || !text.length) {
        return [];
    }

    let nextSubItemStartIndex = 0;
    const formattedData: HighlightableRowTextSubItem[] = [];
    matchingIndices = matchingIndices || [];

    for (let i = 0; i < matchingIndices.length; i++) {
        if (nextSubItemStartIndex !== matchingIndices[i]) {
            formattedData.push(_createItem(text, nextSubItemStartIndex, matchingIndices[i], false));
            nextSubItemStartIndex = matchingIndices[i];
        }

        while (i < matchingIndices.length && matchingIndices[i] + 1 === matchingIndices[i + 1]) {
            ++i;
        }

        formattedData.push(_createItem(text, nextSubItemStartIndex, matchingIndices[i] + 1, true));
        nextSubItemStartIndex = matchingIndices[i] + 1;
    }

    if (nextSubItemStartIndex < text.length) {
        formattedData.push(_createItem(text, nextSubItemStartIndex, text.length, false));
    }

    return formattedData;
}

function _createItem(text: string, start: number, end: number, highlight: boolean): HighlightableRowTextSubItem {
    return {
        text: text.substring(start, end),
        highlight
    };
}
